import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Download,
  FileCheck2,
  HelpCircle,
  Info,
  Landmark,
  Link2,
  Plus,
  ShieldAlert,
} from "lucide-react";
import {
  actions,
  commercialSettlementWorkspace,
  enhancedTransactionEvidencePack,
  settlementEvidenceUrl,
  transactionWorkspace,
  uploadSettlementEvidence,
} from "@/lib/api";
import {
  evidenceCoverage,
  moneyAvailability,
} from "@/lib/product-intelligence.mjs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { displayDate, money, titleCase } from "@/lib/utils";
import { BankStatementReconciliation } from "@/bank-statement-reconciliation";
import { ControlPassportPanel } from "@/components/control-passport-panel";

type Props = {
  organisationId: string;
  refresh: number;
  query: string;
  canFinance: boolean;
  canApprove: boolean;
  viewerRole: string;
  onNavigate: (view: string, recordId?: string) => void;
  changed: () => void;
};
type Workspace = Awaited<ReturnType<typeof transactionWorkspace>> & {
  commercial: Awaited<ReturnType<typeof commercialSettlementWorkspace>>;
};
type HelpTopic =
  | "paid"
  | "unreconciled"
  | "advances"
  | "retentions"
  | "boundary";
const BANK_BOUNDARY = "RAQOZ records execution; it does not move bank funds.";

const HELP: Record<
  HelpTopic,
  {
    title: string;
    meaning: string;
    records: string;
    unavailable: string;
    future: string;
  }
> = {
  paid: {
    title: "Paid",
    meaning:
      "The net value of RAQOZ payment records that an authorised finance user marked paid or reconciled.",
    records:
      "A payment record, completed state transition, external payment reference and immutable lifecycle event.",
    unavailable:
      "Without at least one completed RAQOZ payment record, the system cannot distinguish no payments from payment evidence held elsewhere.",
    future:
      "A bank, ERP or payment-provider integration could make completion authoritative instead of manually evidenced.",
  },
  unreconciled: {
    title: "Paid but unreconciled",
    meaning:
      "RAQOZ-recorded paid value that does not yet have a fully matched reconciliation record.",
    records:
      "A paid payment record plus a reconciliation record with source, reference, amount and matched amount.",
    unavailable:
      "Without a recorded paid state, there is no defensible amount to classify as unreconciled.",
    future:
      "A governed bank-statement or ERP feed could supply authoritative transactions and matching evidence.",
  },
  advances: {
    title: "Advances",
    meaning:
      "Approved commercial schedule lines classified as advances, supported by user-recorded external settlement evidence.",
    records:
      "A maker-checker approved advance schedule plus remittance, UTR or other external evidence recorded by finance.",
    unavailable:
      "RAQOZ does not infer an advance from invoice timing or a lower balance. It remains NOT AVAILABLE until an approved schedule is recorded.",
    future:
      "An ERP or treasury connector could corroborate the user-recorded evidence automatically.",
  },
  retentions: {
    title: "Retentions",
    meaning: "Commercial adjustments explicitly classified as retention.",
    records:
      "An approved commercial schedule showing retention withheld, its release obligation and any separately approved release evidence.",
    unavailable:
      "RAQOZ never infers retention from a short payment. The amount and release condition must be entered and approved explicitly.",
    future:
      "A contract or ERP connector could propose the schedule, while RAQOZ would retain approval and release control.",
  },
  boundary: {
    title: "Payment and reconciliation boundary",
    meaning:
      "RAQOZ controls approved payable states and records evidence supplied by authorised users. It does not execute transfers or independently confirm bank settlement.",
    records:
      "Invoice approval, payment-state transitions, external references, reconciliation source records and immutable events.",
    unavailable:
      "A value is not authoritative bank data unless a governed source system supplied and confirmed it.",
    future:
      "A bank, treasury, payment-provider or ERP connector with signed delivery and reconciliation evidence.",
  },
};

export function TransactionWorkspace({
  organisationId,
  refresh,
  query,
  canFinance,
  canApprove,
  viewerRole,
  onNavigate,
  changed,
}: Props) {
  const [data, setData] = useState<Workspace>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [help, setHelp] = useState<HelpTopic | null>(null);
  const [paymentAction, setPaymentAction] = useState<any>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDecision, setScheduleDecision] = useState<{ row: any; decision: "approved" | "rejected" } | null>(null);
  const [settlementTarget, setSettlementTarget] = useState<{
    paymentId?: string;
    scheduleId?: string;
    label: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [packState, setPackState] = useState<{
    caseRow: any;
    pack: any;
    coverage: any[];
  } | null>(null);

  useEffect(() => {
    let live = true;
    setError("");
    Promise.all([
      transactionWorkspace(organisationId),
      commercialSettlementWorkspace(organisationId),
    ])
      .then(([value, commercial]) => {
        if (live) setData({ ...value, commercial });
      })
      .catch((reason) => {
        if (live) setError(reason.message);
      });
    return () => {
      live = false;
    };
  }, [organisationId, refresh]);

  const filteredPayments = useMemo(
    () =>
      data?.payments.filter((row) =>
        match(
          query,
          row.payment_id,
          row.invoice_no,
          row.po_no,
          row.vendor_name,
          row.state,
        ),
      ) || [],
    [data, query],
  );
  const openExposure = useMemo(() => {
    const values = new Map<string, number>();
    for (const row of data?.payments || [])
      if (!["paid", "reconciled", "cancelled"].includes(row.state))
        values.set(
          row.currency,
          (values.get(row.currency) || 0) + Number(row.net_amount),
        );
    return [...values.entries()];
  }, [data]);
  const availability = useMemo(
    () => {
      const base = moneyAvailability(data?.payments || [], data?.adjustments || []);
      const schedules = data?.commercial?.schedules || [];
      const byCurrency = (selector: (row: any) => number) => {
        const values: Record<string, number> = {};
        for (const row of schedules) {
          const amount = selector(row);
          if (amount)
            values[row.currency] = (values[row.currency] || 0) + amount;
        }
        return values;
      };
      const advances = byCurrency((row) =>
        row.kind === "advance" ? Number(row.settled_amount || 0) : 0,
      );
      const retentions = byCurrency((row) =>
        Number(row.retention_withheld_amount || 0) -
        (row.kind === "retention_release" ? Number(row.settled_amount || 0) : 0),
      );
      return {
        ...base,
        advances: Object.keys(advances).length
          ? { available: true, values: advances, records: schedules.filter((row: any) => row.kind === "advance").length }
          : base.advances,
        retentions: Object.keys(retentions).length
          ? { available: true, values: retentions, records: schedules.filter((row: any) => Number(row.retention_withheld_amount || 0) > 0 || row.kind === "retention_release").length }
          : base.retentions,
      };
    },
    [data],
  );

  async function run(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    setError("");
    try {
      await work();
      changed();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The controlled action could not be completed.",
      );
    } finally {
      setBusy("");
    }
  }

  async function preparePack(caseRow: any) {
    setBusy(caseRow.id);
    setError("");
    try {
      const pack = await enhancedTransactionEvidencePack(
        organisationId,
        caseRow.id,
      );
      const source = {
        ...(pack as any).source_pack,
        ...(pack as any).ancestry,
        links: (pack as any).ancestry?.transaction_links,
      };
      setPackState({ caseRow, pack, coverage: evidenceCoverage(source) });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The evidence pack could not be prepared.",
      );
    } finally {
      setBusy("");
    }
  }

  function downloadPack() {
    if (!packState) return;
    const exportPayload = {
      ...packState.pack,
      evidenceCoverage: packState.coverage,
      evidenceCoverageIntegrity:
        "Derived by the RC2 client and not included in source_pack.value_assurance_manifest.",
    };
    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `RAQOZ-${packState.caseRow.case_reference}-evidence-pack-v3.1.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (error && !data)
    return <Notice title="Unable to load Money in Motion">{error}</Notice>;
  if (!data)
    return (
      <Notice title="Loading transaction control">
        Retrieving the tenant-scoped transaction graph…
      </Notice>
    );
  const openExceptions = data.matches.reduce(
    (sum, row) => sum + Number(row.open_exceptions || 0),
    0,
  );

  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <p className="eyebrow">TRANSACTION CONTROL</p>
          <h1>Money in Motion</h1>
          <p>
            Approved payables, user-recorded payment evidence, reconciliation
            and closure—without overstating bank authority. {BANK_BOUNDARY}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canFinance && (
            <Button onClick={() => setScheduleOpen(true)}>
              <Plus className="size-4" />
              Add commercial schedule
            </Button>
          )}
          <Button variant="outline" onClick={() => setHelp("boundary")}>
            <HelpCircle className="size-4" />
            Learn why
          </Button>
        </div>
      </header>

      {error && (
        <div
          className="rounded-xl border border-[var(--error-border)] bg-[var(--error-soft)] p-4 text-sm text-[var(--error)]"
          role="alert"
        >
          {error}
        </div>
      )}

      <section
        className="boundary-panel"
        aria-labelledby="payment-boundary-title"
      >
        <ShieldAlert className="size-5" aria-hidden="true" />
        <div>
          <h2 id="payment-boundary-title">
            Payment and reconciliation boundary
          </h2>
          <p>
            RAQOZ can control approved payables and record
            payment/reconciliation evidence supplied by authorised finance
            users. It does not execute bank payments, read a bank feed or
            independently prove settlement. Values below are marked NOT
            AVAILABLE whenever the current records cannot support them.
          </p>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AvailabilityMetric
          label="Paid"
          value={availability.paid}
          topic="paid"
          onHelp={setHelp}
        />
        <AvailabilityMetric
          label="Paid but unreconciled"
          value={availability.unreconciled}
          topic="unreconciled"
          onHelp={setHelp}
        />
        <AvailabilityMetric
          label="Advances"
          value={availability.advances}
          topic="advances"
          onHelp={setHelp}
        />
        <AvailabilityMetric
          label="Retentions"
          value={availability.retentions}
          topic="retentions"
          onHelp={setHelp}
        />
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <CountMetric
          label="Open payment records"
          value={
            data.payments.filter(
              (row) => !["reconciled", "cancelled"].includes(row.state),
            ).length
          }
          note="Recorded states awaiting completion or reconciliation"
        />
        <CountMetric
          label="Open match exceptions"
          value={openExceptions}
          note="Recorded exceptions requiring review"
          alert={openExceptions > 0}
        />
        <CountMetric
          label="Closed cases"
          value={
            data.cases.filter((row) => row.closure_status === "closed").length
          }
          note={`Across ${data.cases.length} recorded transaction cases`}
        />
        <CountMetric
          label="Open exposure"
          value={
            openExposure.length
              ? openExposure
                  .map(([currency, value]) => money(value, currency))
                  .join(" · ")
              : "NOT AVAILABLE"
          }
          note={
            openExposure.length
              ? "RAQOZ-recorded unpaid states"
              : "No open payment evidence is recorded"
          }
        />
      </section>

      <Section
        title="Commercial position"
        note="PO value, approved advances, retention and the remaining recorded balance. Values are evidence-backed RAQOZ records, not a general-ledger balance."
      >
        {data.commercial.positions.length ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>PO / supplier</th><th className="numeric">PO value</th><th className="numeric">Advance paid</th><th className="numeric">Retention held</th><th className="numeric">Retention released</th><th className="numeric">Remaining recorded</th><th>Next due</th></tr></thead>
              <tbody>{data.commercial.positions.map((row: any) => <tr key={row.purchase_order_id}><td><strong>{row.po_no}</strong><small className="block text-[var(--text-secondary)]">{row.vendor_name}</small></td><td className="numeric">{money(row.purchase_order_value,row.currency)}</td><td className="numeric">{money(row.advance_paid,row.currency)}</td><td className="numeric">{money(row.retention_withheld,row.currency)}</td><td className="numeric">{money(row.retention_released,row.currency)}</td><td className="numeric font-semibold">{money(row.remaining_recorded,row.currency)}</td><td>{row.next_due_on?displayDate(row.next_due_on):"—"}</td></tr>)}</tbody>
            </table>
          </div>
        ) : <Empty>No issued purchase order has a commercial position yet.</Empty>}
      </Section>

      <Section
        title="Advance, milestone, recurring and retention schedules"
        note="Every manually added schedule requires independent approval. Retention releases also require the linked obligation to be accepted."
      >
        {data.commercial.schedules.length ? (
          <>
            <div className="hidden md:block">
              <table className="data-table">
                <thead>
                  <tr><th>Schedule</th><th>PO / supplier</th><th className="numeric">Gross</th><th>Deductions</th><th className="numeric">Net due</th><th>Due</th><th>Status</th><th>Control</th></tr>
                </thead>
                <tbody>
                  {data.commercial.schedules.map((row: any) => (
                    <tr key={row.id}>
                      <td><strong>{row.title}</strong><small className="block text-[var(--text-secondary)]">{titleCase(row.kind)}{row.commitment_title ? ` · ${row.commitment_title}` : ""}</small></td>
                      <td>{row.po_no || "Recurring commitment"}<small className="block text-[var(--text-secondary)]">{row.vendor_name}</small></td>
                      <td className="numeric">{money(row.gross_amount, row.currency)}</td>
                      <td><small className="block">Advance recovery {money(row.advance_recovery_amount, row.currency)}</small><small className="block">Retention {money(row.retention_withheld_amount, row.currency)}</small><small className="block">Withholding {money(row.withholding_amount, row.currency)}</small></td>
                      <td className="numeric font-semibold">{money(row.net_due, row.currency)}<small className="block text-[var(--text-secondary)]">Settled {money(row.settled_amount, row.currency)}</small></td>
                      <td>{displayDate(row.due_on)}</td>
                      <td><Badge value={row.status} /><small className="mt-1 block text-[var(--text-secondary)]">Approvals {row.approval_count}/{row.required_approvals}</small></td>
                      <td><ScheduleActions row={row} busy={busy} canFinance={canFinance} canApprove={canApprove} onRun={run} organisationId={organisationId} onDecision={setScheduleDecision} onEvidence={setSettlementTarget} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid gap-3 md:hidden" aria-label="Commercial payment schedules">
              {data.commercial.schedules.map((row: any) => (
                <article key={row.id} className="rounded-xl border border-[var(--border)] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div><strong>{row.title}</strong><p className="mt-1 text-xs text-[var(--text-secondary)]">{titleCase(row.kind)} · {row.po_no || "Recurring commitment"} · {row.vendor_name}</p></div>
                    <Badge value={row.status} />
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-xs text-[var(--text-muted)]">Net due</dt><dd className="mt-1 font-semibold tabular-nums">{money(row.net_due, row.currency)}</dd></div>
                    <div><dt className="text-xs text-[var(--text-muted)]">Due</dt><dd className="mt-1">{displayDate(row.due_on)}</dd></div>
                    <div><dt className="text-xs text-[var(--text-muted)]">Settled</dt><dd className="mt-1 tabular-nums">{money(row.settled_amount, row.currency)}</dd></div>
                    <div><dt className="text-xs text-[var(--text-muted)]">Approvals</dt><dd className="mt-1">{row.approval_count}/{row.required_approvals}</dd></div>
                  </dl>
                  <div className="mt-4 border-t border-[var(--border)] pt-3">
                    <ScheduleActions row={row} busy={busy} canFinance={canFinance} canApprove={canApprove} onRun={run} organisationId={organisationId} onDecision={setScheduleDecision} onEvidence={setSettlementTarget} />
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <Empty>No commercial schedule is recorded. Add advances, milestones, recurring dues, final balances or retention releases here.</Empty>
        )}
      </Section>

      <Section title="Settlement evidence" note="Remittance advice and other proof can be supplier-visible; bank confirmations remain private unless finance explicitly shares them.">
        {data.commercial.evidence.length?<div className="overflow-x-auto"><table className="data-table"><thead><tr><th>Evidence</th><th>Reference</th><th>Date</th><th className="numeric">Amount</th><th>Visibility</th><th>Document</th></tr></thead><tbody>{data.commercial.evidence.map((row:any)=><tr key={row.id}><td><Badge value={row.evidence_type}/></td><td><strong>{row.external_reference}</strong><small className="block text-[var(--text-secondary)]">{row.note||"Authorised finance record"}</small></td><td>{displayDate(row.settlement_date)}</td><td className="numeric">{money(row.amount,row.currency)}</td><td>{row.supplier_visible?"Supplier visible":"Internal only"}</td><td>{row.object_path?<Button size="sm" variant="outline" onClick={async()=>{try{window.open(await settlementEvidenceUrl(row.object_path),"_blank","noopener");}catch(reason){setError((reason as Error).message);}}}>View proof</Button>:"Reference only"}</td></tr>)}</tbody></table></div>:<Empty>No remittance or settlement evidence has been recorded.</Empty>}
      </Section>

      <BankStatementReconciliation
        organisationId={organisationId}
        refresh={refresh}
        payments={data.payments}
        canFinance={canFinance}
        changed={changed}
      />

      <Section
        title="Payment state evidence"
        note="Transitions are permission checked and written to the audit history. External references are evidence supplied to RAQOZ—not independent bank confirmation."
      >
        {filteredPayments.length ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice / PO</th>
                  <th>Supplier</th>
                  <th className="numeric">Net amount</th>
                  <th>Due</th>
                  <th>State</th>
                  <th>Next control</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((row) => (
                  <tr key={row.payment_id}>
                    <td>
                      <strong>{row.invoice_no}</strong>
                      <small className="block text-[var(--text-secondary)]">
                        {row.po_no}
                      </small>
                    </td>
                    <td>{row.vendor_name}</td>
                    <td className="numeric font-semibold">
                      {money(row.net_amount, row.currency)}
                    </td>
                    <td>
                      {row.due_date ? (
                        displayDate(row.due_date)
                      ) : (
                        <span className="unavailable">NOT AVAILABLE</span>
                      )}
                    </td>
                    <td>
                      <Badge value={row.state} />
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {canFinance && nextLabel(row.state) && (
                          <Button
                            size="sm"
                            disabled={busy === row.payment_id}
                            onClick={() => setPaymentAction(row)}
                          >
                            {nextLabel(row.state)}
                          </Button>
                        )}
                        {canFinance && ["paid", "reconciled"].includes(row.state) && (
                          <Button size="sm" variant="outline" onClick={() => setSettlementTarget({paymentId:row.payment_id,label:`${row.invoice_no} · ${row.po_no}`,amount:Number(row.net_amount),currency:row.currency})}>
                            Add proof
                          </Button>
                        )}
                        {!canFinance && <span className="text-xs text-[var(--text-muted)]">{row.state === "reconciled" ? "Reconciled" : "No permitted action"}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            No approved matched invoice has created a payment record. Financial
            totals remain NOT AVAILABLE.
          </Empty>
        )}
      </Section>

      <Section
        title="Three-way match"
        note="Purchase order, accepted receipt value and invoice result are shown from recorded procurement evidence."
      >
        {data.matches.length ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Supplier</th>
                  <th className="numeric">PO value</th>
                  <th className="numeric">Accepted value</th>
                  <th>Result</th>
                  <th className="numeric">Exceptions</th>
                </tr>
              </thead>
              <tbody>
                {data.matches.map((row) => (
                  <tr key={row.invoice_id}>
                    <td>
                      <strong>{row.invoice_no}</strong>
                      <small className="block text-[var(--text-secondary)]">
                        {money(
                          Number(row.amount) + Number(row.tax_amount),
                          row.currency,
                        )}
                      </small>
                    </td>
                    <td>{row.vendor_name}</td>
                    <td className="numeric">
                      {money(row.purchase_order_total, row.currency)}
                    </td>
                    <td className="numeric">
                      {money(row.accepted_value, row.currency)}
                    </td>
                    <td>
                      <Badge
                        value={row.latest_match_result || row.invoice_status}
                      />
                    </td>
                    <td className="numeric">{row.open_exceptions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No invoice match evidence is recorded.</Empty>
        )}
      </Section>

      <section className="grid gap-4 xl:grid-cols-[.85fr_1.15fr]">
        <Section
          title="Transaction evidence packs"
          note="Inspect explicit evidence coverage before downloading the versioned JSON envelope."
        >
          <div className="grid gap-2 p-3">
            {data.cases.map((row) => (
              <article
                className="rounded-xl border border-[var(--border)] p-3"
                key={row.id}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <strong className="text-sm">{row.case_reference}</strong>
                    <small className="block text-[var(--text-secondary)]">
                      {titleCase(row.operational_status)} ·{" "}
                      {titleCase(row.financial_status)}
                    </small>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === row.id}
                      onClick={() => preparePack(row)}
                    >
                      <FileCheck2 className="size-4" />
                      Inspect pack
                    </Button>
                    {canFinance && row.closure_status !== "closed" && (
                      <Button
                        size="sm"
                        disabled={busy === row.id}
                        onClick={() =>
                          run(row.id, () =>
                            actions.closeProcurementCase(
                              organisationId,
                              row.id,
                            ),
                          )
                        }
                      >
                        Close case
                      </Button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {!data.cases.length && <Empty>No transaction case exists yet.</Empty>}
        </Section>
        <Section
          title="Unified transaction timeline"
          note="Lifecycle events preserve actor role, source entity and state transition."
        >
          <div className="max-h-[430px] overflow-auto px-4">
            {data.timeline
              .filter((row) =>
                match(
                  query,
                  row.event_id,
                  row.event_type,
                  row.entity_type,
                  row.reason,
                ),
              )
              .map((row) => (
                <div
                  className="grid grid-cols-[8px_1fr_auto] gap-3 border-b border-[var(--border)] py-3"
                  key={row.event_id}
                >
                  <i className="mt-1 size-2 rounded-full bg-[var(--teal)]" />
                  <div>
                    <strong className="text-sm">
                      {titleCase(row.event_type)}
                    </strong>
                    <small className="block text-[var(--text-secondary)]">
                      {titleCase(row.entity_type)} ·{" "}
                      {titleCase(row.actor_role || "system")}
                      {row.previous_state || row.new_state
                        ? ` · ${titleCase(row.previous_state || "new")} → ${titleCase(row.new_state || "recorded")}`
                        : ""}
                    </small>
                  </div>
                  <time
                    className="text-xs text-[var(--text-muted)]"
                    dateTime={row.created_at}
                  >
                    {displayDate(row.created_at, true)}
                  </time>
                </div>
              ))}
          </div>
        </Section>
      </section>

      <HelpDialog
        topic={help}
        onOpenChange={(open) => !open && setHelp(null)}
      />
      <PaymentActionDialog
        row={paymentAction}
        organisationId={organisationId}
        viewerRole={viewerRole}
        canFinance={canFinance}
        canApprove={canApprove}
        onNavigate={onNavigate}
        open={Boolean(paymentAction)}
        busy={Boolean(paymentAction && busy === paymentAction.payment_id)}
        onOpenChange={(open) => !open && setPaymentAction(null)}
        onSubmit={async (input) => {
          const row = paymentAction;
          if (!row) return;
          await run(row.payment_id, () =>
            paymentWork(organisationId, row, input),
          );
          setPaymentAction(null);
        }}
      />
      <CommercialScheduleDecisionDialog
        value={scheduleDecision}
        busy={Boolean(scheduleDecision && busy === scheduleDecision.row.id)}
        onOpenChange={(open) => !open && setScheduleDecision(null)}
        onSubmit={async (reason) => {
          if (!scheduleDecision) return;
          await run(scheduleDecision.row.id, () =>
            actions.approveCommercialPaymentSchedule(
              organisationId,
              scheduleDecision.row.id,
              scheduleDecision.decision,
              reason,
            ),
          );
          setScheduleDecision(null);
        }}
      />
      <CommercialScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        workspace={data.commercial}
        busy={Boolean(busy)}
        onSubmit={async (definition) => {
          await run("new-commercial-schedule", async () => {
            const created: any = await actions.createCommercialPaymentSchedule(organisationId,definition);
            await actions.submitCommercialPaymentSchedule(organisationId,created.id);
          });
          setScheduleOpen(false);
        }}
      />
      <SettlementEvidenceDialog
        target={settlementTarget}
        open={Boolean(settlementTarget)}
        busy={Boolean(settlementTarget && busy === (settlementTarget.paymentId || settlementTarget.scheduleId))}
        onOpenChange={(open) => !open && setSettlementTarget(null)}
        onSubmit={async (input) => {
          if (!settlementTarget) return;
          const id=settlementTarget.paymentId||settlementTarget.scheduleId||"settlement";
          await run(id,()=>uploadSettlementEvidence(organisationId,settlementTarget,input));
          setSettlementTarget(null);
        }}
      />
      <EvidenceDialog
        value={packState}
        open={Boolean(packState)}
        onOpenChange={(open) => !open && setPackState(null)}
        onDownload={downloadPack}
      />
    </div>
  );
}

function AvailabilityMetric({
  label,
  value,
  topic,
  onHelp,
}: {
  label: string;
  value: any;
  topic: HelpTopic;
  onHelp: (topic: HelpTopic) => void;
}) {
  return (
    <article className="surface p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-[var(--text-secondary)]">{label}</span>
        <button
          className="metric-help"
          onClick={() => onHelp(topic)}
          aria-label={`Explain ${label}`}
          title={`Explain ${label}`}
        >
          <Info className="size-4" />
        </button>
      </div>
      {value.available ? (
        <strong className="my-2 block text-xl font-semibold tabular-nums">
          {Object.entries(value.values)
            .map(([currency, amount]) => money(Number(amount), currency))
            .join(" · ")}
        </strong>
      ) : (
        <strong className="unavailable my-3 block">NOT AVAILABLE</strong>
      )}
      <span className="text-xs text-[var(--text-secondary)]">
        {value.available
          ? `${value.records} recorded source ${value.records === 1 ? "record" : "records"}`
          : value.reason}
      </span>
    </article>
  );
}

function CountMetric({
  label,
  value,
  note,
  alert = false,
}: {
  label: string;
  value: string | number;
  note: string;
  alert?: boolean;
}) {
  return (
    <article
      className={`surface p-4 ${alert ? "border-[var(--warning-border)]" : ""}`}
    >
      <span className="text-xs text-[var(--text-secondary)]">{label}</span>
      <strong className="my-2 block text-xl font-semibold tabular-nums">
        {value}
      </strong>
      <span
        className={`text-xs ${alert ? "text-[var(--warning)]" : "text-[var(--text-secondary)]"}`}
      >
        {note}
      </span>
    </article>
  );
}

function HelpDialog({
  topic,
  onOpenChange,
}: {
  topic: HelpTopic | null;
  onOpenChange: (open: boolean) => void;
}) {
  const value = topic ? HELP[topic] : null;
  return (
    <Dialog open={Boolean(value)} onOpenChange={onOpenChange}>
      <DialogContent>
        {value && (
          <>
            <DialogHeader
              eyebrow="DATA PROVENANCE"
              title={value.title}
              description="What RAQOZ can prove today and what remains integration-dependent."
            />
            <div className="grid gap-4 p-6">
              <HelpRow icon={<Landmark className="size-4" />} title="Meaning">
                {value.meaning}
              </HelpRow>
              <HelpRow
                icon={<FileCheck2 className="size-4" />}
                title="Required source records"
              >
                {value.records}
              </HelpRow>
              <HelpRow
                icon={<AlertTriangle className="size-4" />}
                title="Why it may be unavailable"
              >
                {value.unavailable}
              </HelpRow>
              <HelpRow
                icon={<Link2 className="size-4" />}
                title="What would make it authoritative"
              >
                {value.future}
              </HelpRow>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HelpRow({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 rounded-xl border border-[var(--border)] p-4">
      <span className="section-heading__icon">{icon}</span>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-[var(--text-secondary)]">
          {children}
        </p>
      </div>
    </div>
  );
}

function ScheduleActions({
  row,
  busy,
  canFinance,
  canApprove,
  onRun,
  organisationId,
  onDecision,
  onEvidence,
}: {
  row: any;
  busy: string;
  canFinance: boolean;
  canApprove: boolean;
  onRun: (id: string, work: () => Promise<unknown>) => Promise<void>;
  organisationId: string;
  onDecision: (value: { row: any; decision: "approved" | "rejected" }) => void;
  onEvidence: (value: { scheduleId: string; label: string; amount: number; currency: string }) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {canFinance && row.status === "draft" && (
        <Button size="sm" variant="outline" disabled={busy === row.id} onClick={() => void onRun(row.id, () => actions.submitCommercialPaymentSchedule(organisationId, row.id))}>
          Submit
        </Button>
      )}
      {canApprove && row.status === "pending_approval" && (
        <>
          <Button size="sm" disabled={busy === row.id} onClick={() => onDecision({ row, decision: "approved" })}>
            Approve
          </Button>
          <Button size="sm" variant="outline" disabled={busy === row.id} onClick={() => onDecision({ row, decision: "rejected" })}>
            Reject
          </Button>
        </>
      )}
      {canFinance &&
        ["approved", "due", "settled", "reconciled"].includes(row.status) &&
        Number(row.settled_amount || 0) < Number(row.net_due) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onEvidence({
                scheduleId: row.id,
                label: row.title,
                amount: Number(row.net_due) - Number(row.settled_amount || 0),
                currency: row.currency,
              })
            }
          >
            Add proof
          </Button>
        )}
      {!canFinance && !canApprove && (
        <span className="text-xs text-[var(--text-muted)]">No permitted action</span>
      )}
    </div>
  );
}

function CommercialScheduleDecisionDialog({
  value,
  busy,
  onOpenChange,
  onSubmit,
}: {
  value: { row: any; decision: "approved" | "rejected" } | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  return (
    <Dialog open={Boolean(value)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="INDEPENDENT COMMERCIAL DECISION"
          title={value ? `${titleCase(value.decision)} ${value.row.title}` : "Commercial schedule decision"}
          description="The reason is recorded with the maker-checker decision. No payment instruction is created."
        />
        <form
          className="grid gap-4 p-6"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const reason = String(new FormData(event.currentTarget).get("reason") || "").trim();
            void onSubmit(reason);
          }}
        >
          <Label>
            {value?.decision === "approved" ? "Approval basis" : "Rejection reason"}
            <Textarea name="reason" required minLength={10} maxLength={4000} />
          </Label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant={value?.decision === "rejected" ? "destructive" : "default"} disabled={busy}>
              {busy ? "Recording…" : `Confirm ${value?.decision || "decision"}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CommercialScheduleDialog({
  open,
  onOpenChange,
  workspace,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: any;
  busy: boolean;
  onSubmit: (definition: Record<string, unknown>) => Promise<void>;
}) {
  const [kind,setKind]=useState("advance");
  const firstPo=workspace.purchase_orders?.[0];
  async function submit(event:FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form=new FormData(event.currentTarget);
    const po=workspace.purchase_orders.find((row:any)=>row.id===form.get("purchaseOrder"));
    await onSubmit({
      purchase_order_id:form.get("purchaseOrder")||null,
      vendor_id:po?.vendor_id||null,
      kind,
      title:form.get("title"),
      currency:String(form.get("currency")||po?.currency||"INR").toUpperCase(),
      gross_amount:Number(form.get("gross")),
      tax_amount:Number(form.get("tax")||0),
      advance_recovery_amount:Number(form.get("advanceRecovery")||0),
      retention_withheld_amount:Number(form.get("retention")||0),
      withholding_amount:Number(form.get("withholding")||0),
      due_on:form.get("dueOn"),
      release_obligation_id:form.get("releaseObligation")||null,
      release_condition:form.get("releaseCondition")||null,
      source:"manual",
    });
  }
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader eyebrow="COMMERCIAL CONTROL" title="Add payment schedule" description="Manual schedules are submitted immediately for independent approval. No bank instruction is created."/><form className="grid gap-4 p-6" onSubmit={submit}><Label>Purchase order<Select name="purchaseOrder" required defaultValue={firstPo?.id}>{workspace.purchase_orders.map((row:any)=><option key={row.id} value={row.id}>{row.po_no} · {money(row.total,row.currency)}</option>)}</Select></Label><div className="grid gap-4 md:grid-cols-2"><Label>Schedule type<Select value={kind} onChange={(event)=>setKind(event.target.value)}>{["advance","milestone","recurring","final","retention_release","deposit"].map(value=><option key={value} value={value}>{titleCase(value)}</option>)}</Select></Label><Label>Title<Input name="title" required minLength={2} placeholder="20% mobilisation advance"/></Label><Label>Currency<Input name="currency" defaultValue={firstPo?.currency||"INR"} maxLength={3} pattern="[A-Za-z]{3}" required/></Label><Label>Gross amount<Input name="gross" type="number" min="0.01" step="0.01" required/></Label><Label>Tax amount<Input name="tax" type="number" min="0" step="0.01" defaultValue="0"/></Label><Label>Advance recovery<Input name="advanceRecovery" type="number" min="0" step="0.01" defaultValue="0"/></Label><Label>Retention withheld<Input name="retention" type="number" min="0" step="0.01" defaultValue="0"/></Label><Label>Other withholding<Input name="withholding" type="number" min="0" step="0.01" defaultValue="0"/></Label><Label>Due on<Input name="dueOn" type="date" required/></Label></div>{kind==="retention_release"&&<><Label>Release obligation<Select name="releaseObligation" required><option value="">Select accepted release condition</option>{workspace.obligations.map((row:any)=><option key={row.id} value={row.id}>{row.title} · {titleCase(row.status)}</option>)}</Select></Label><Label>Release condition<Textarea name="releaseCondition" required minLength={5} placeholder="Defect-liability period completed and final acceptance recorded"/></Label></>}<div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy||!workspace.purchase_orders.length}>{busy?"Submitting…":"Submit for approval"}</Button></div></form></DialogContent></Dialog>;
}

function SettlementEvidenceDialog({
  target,
  open,
  busy,
  onOpenChange,
  onSubmit,
}: {
  target:{paymentId?:string;scheduleId?:string;label:string;amount:number;currency:string}|null;
  open:boolean;
  busy:boolean;
  onOpenChange:(open:boolean)=>void;
  onSubmit:(input:{evidenceType:string;externalReference:string;settlementDate:string;amount:number;currency:string;supplierVisible:boolean;note:string;file:File|null})=>Promise<void>;
}) {
  const [file,setFile]=useState<File|null>(null);
  useEffect(()=>setFile(null),[target]);
  if(!target) return null;
  async function submit(event:FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form=new FormData(event.currentTarget);
    await onSubmit({
      evidenceType:String(form.get("evidenceType")),
      externalReference:String(form.get("externalReference")),
      settlementDate:String(form.get("settlementDate")),
      amount:Number(form.get("amount")),
      currency:target!.currency,
      supplierVisible:Boolean(form.get("supplierVisible")),
      note:String(form.get("note")||""),
      file,
    });
  }
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader eyebrow="SETTLEMENT EVIDENCE" title={`Record proof · ${target.label}`} description="The bank executes the payment. RAQOZ records the reference, proof and authorised visibility."/><form className="grid gap-4 p-6" onSubmit={submit}><Label>Evidence type<Select name="evidenceType"><option value="remittance_advice">Remittance advice</option><option value="utr_confirmation">UTR confirmation</option><option value="bank_confirmation">Bank confirmation</option><option value="cheque">Cheque</option><option value="payment_provider_confirmation">Payment provider confirmation</option><option value="other">Other</option></Select></Label><Label>External reference<Input name="externalReference" required minLength={3}/></Label><div className="grid gap-4 md:grid-cols-2"><Label>Settlement date<Input name="settlementDate" type="date" defaultValue={new Date().toISOString().slice(0,10)} required/></Label><Label>Amount ({target.currency})<Input name="amount" type="number" min="0.01" max={target.amount} step="0.01" defaultValue={target.amount} required/></Label></div><Label>Proof document (optional)<Input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(event)=>setFile(event.target.files?.[0]||null)}/></Label><Label>Finance note<Textarea name="note" placeholder="What was checked and where the external payment was executed"/></Label><label className="flex items-start gap-3 rounded-xl border border-[var(--border)] p-3 text-sm"><input className="mt-1" name="supplierVisible" type="checkbox"/><span><strong>Share this proof with the supplier</strong><small className="mt-1 block text-[var(--text-secondary)]">Use for remittance advice. Leave bank statements and internal confirmations private.</small></span></label><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy}>{busy?"Recording…":"Record settlement proof"}</Button></div></form></DialogContent></Dialog>;
}

function PaymentActionDialog({
  row,
  organisationId,
  viewerRole,
  canFinance,
  canApprove,
  onNavigate,
  open,
  busy,
  onOpenChange,
  onSubmit,
}: {
  row: any;
  organisationId: string;
  viewerRole: string;
  canFinance: boolean;
  canApprove: boolean;
  onNavigate: (view: string, recordId?: string) => void;
  open: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: Record<string, string>) => Promise<void>;
}) {
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [bankReference, setBankReference] = useState("");
  useEffect(() => {
    if (row) {
      setDate(row.scheduled_date || row.due_date || "");
      setReference(row.external_payment_reference || "");
      setBankReference("");
    }
  }, [row]);
  if (!row) return null;
  const needsDate = ["invoice_accepted", "due"].includes(row.state);
  const needsReference = [
    "payment_approved",
    "initiated",
    "bank_processing",
  ].includes(row.state);
  const needsBank = row.state === "paid";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="CONTROLLED FINANCE ACTION"
          title={nextLabel(row.state) || "Payment action"}
          description={`${row.invoice_no} · ${money(row.net_amount, row.currency)} · current state ${titleCase(row.state)}`}
        />
        <div className="px-6 pt-5">
          <ControlPassportPanel
            organisationId={organisationId}
            checkpoint="payment"
            caseId={row.procurement_case_id}
            viewerRole={viewerRole}
            capabilities={{ finance: canFinance, approve: canApprove }}
            onNavigate={onNavigate}
            compact
          />
        </div>
        <form
          className="grid gap-4 p-6"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            onSubmit({ date, reference, bankReference });
          }}
        >
          {needsDate && (
            <Label>
              Scheduled payment date
              <Input
                type="date"
                required
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Label>
          )}
          {needsReference && (
            <Label>
              External payment or provider reference
              <Input
                required
                minLength={3}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
              <span className="font-normal text-[var(--text-secondary)]">
                This is evidence supplied to RAQOZ; it is not independently
                bank-verified.
              </span>
            </Label>
          )}
          {needsBank && (
            <>
              <Label>
                Reconciliation source
                <Input readOnly value="Authorised bank-statement evidence" />
              </Label>
              <Label>
                Bank statement reference
                <Input
                  required
                  minLength={3}
                  value={bankReference}
                  onChange={(event) => setBankReference(event.target.value)}
                />
              </Label>
            </>
          )}
          {!needsDate && !needsReference && !needsBank && (
            <div className="boundary-panel">
              <Banknote className="size-5" />
              <div>
                <h2>Approval</h2>
                <p>
                  This records an authorised approval event. It does not
                  instruct a bank.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Recording…" : "Confirm controlled action"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EvidenceDialog({
  value,
  open,
  onOpenChange,
  onDownload,
}: {
  value: { caseRow: any; pack: any; coverage: any[] } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(900px,calc(100vw-2rem))]">
        {value && (
          <>
            <DialogHeader
              eyebrow="TRANSACTION EVIDENCE"
              title={`${value.caseRow.case_reference} coverage`}
              description="The pack states what is available, partially available, not recorded or unsupported. It is not described as complete."
            />
            <div className="max-h-[60vh] overflow-auto p-5">
              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Evidence category</th>
                    <th>Status</th>
                    <th className="numeric">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {value.coverage.map((row) => (
                    <tr key={row.category}>
                      <td>{row.category}</td>
                      <td>
                        <Badge value={row.status} />
                      </td>
                      <td className="numeric">{row.recordCount ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="boundary-panel mx-5 mb-5">
              <Info className="size-5" />
              <div>
                <h2>Integrity scope</h2>
                <p>
                  The database SHA-256 manifest covers source_pack only. The RC2
                  ancestry envelope and evidenceCoverage section are explicitly
                  outside that hash and say so in the export.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[var(--border)] p-5">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={onDownload}>
                <Download className="size-4" />
                Download JSON
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function paymentWork(
  organisationId: string,
  row: any,
  input: Record<string, string>,
) {
  if (["invoice_accepted", "due"].includes(row.state))
    return actions.proposePayment(organisationId, row.payment_id, input.date);
  if (row.state === "payment_proposed")
    return actions.approvePayment(organisationId, row.payment_id);
  if (row.state === "payment_approved")
    return actions.recordPaymentInitiated(
      organisationId,
      row.payment_id,
      input.reference,
    );
  if (["initiated", "bank_processing"].includes(row.state))
    return actions.recordPaymentCompleted(
      organisationId,
      row.payment_id,
      input.reference,
    );
  if (row.state === "paid")
    return actions.reconcilePayment(
      organisationId,
      row.payment_id,
      "bank_statement",
      row.external_payment_reference || "",
      input.bankReference,
      Number(row.net_amount),
    );
  return Promise.resolve();
}

function nextLabel(state: string) {
  return (
    {
      invoice_accepted: "Propose payment",
      due: "Propose payment",
      payment_proposed: "Approve payment",
      payment_approved: "Record initiation",
      initiated: "Record paid evidence",
      bank_processing: "Record paid evidence",
      paid: "Record reconciliation",
    } as Record<string, string>
  )[state];
}
function match(query: string, ...values: unknown[]) {
  return (
    !query ||
    values.some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
  );
}
function Section({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}) {
  return (
    <section className="surface overflow-hidden">
      <header className="section-heading">
        <span className="section-heading__icon">
          <CheckCircle2 className="size-4" />
        </span>
        <div>
          <h2>{title}</h2>
          <p>{note}</p>
        </div>
      </header>
      {children}
    </section>
  );
}
function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="p-8 text-center text-sm text-[var(--text-secondary)]">
      {children}
    </p>
  );
}
function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="surface p-8">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm text-[var(--text-secondary)]">{children}</p>
    </div>
  );
}
